import React, { createContext, useContext, useState, useCallback } from "react";

interface SelectionState {
  selectedBOMRows: any[];
  modalFilters: {
    guandaId: string;
    componentCode: string;
    distinction: string;
    metricSizeMark: string;
    transportNumber: string;
  };
  modalCurrentPage: number;
  modalPageSize: number;
}

interface SelectionContextType {
  selectionState: SelectionState;
  setSelectedBOMRows: (rows: any[]) => void;
  setModalFilters: (filters: SelectionState["modalFilters"]) => void;
  setModalCurrentPage: (page: number) => void;
  setModalPageSize: (size: number) => void;
  clearSelection: () => void;
}

const defaultState: SelectionState = {
  selectedBOMRows: [],
  modalFilters: {
    guandaId: "",
    componentCode: "",
    distinction: "",
    metricSizeMark: "",
    transportNumber: "",
  },
  modalCurrentPage: 1,
  modalPageSize: 10,
};

const SelectionContext = createContext<SelectionContextType | undefined>(
  undefined,
);

export const SelectionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectionState, setSelectionState] = useState<SelectionState>(() => {
    const saved = localStorage.getItem("productionPlanSelection");
    if (saved) {
      const parsedState = JSON.parse(saved);
      // Clean up any old buildList property that might exist
      if (
        parsedState.modalFilters &&
        parsedState.modalFilters.hasOwnProperty("buildList")
      ) {
        delete parsedState.modalFilters.buildList;
      }
      // Add componentCode if it doesn't exist
      if (
        parsedState.modalFilters &&
        !parsedState.modalFilters.hasOwnProperty("componentCode")
      ) {
        parsedState.modalFilters.componentCode = "";
      }
      // Migrate installationLocation to distinction
      if (
        parsedState.modalFilters &&
        parsedState.modalFilters.hasOwnProperty("installationLocation")
      ) {
        parsedState.modalFilters.distinction =
          parsedState.modalFilters.installationLocation;
        delete parsedState.modalFilters.installationLocation;
      }
      // Add distinction if it doesn't exist
      if (
        parsedState.modalFilters &&
        !parsedState.modalFilters.hasOwnProperty("distinction")
      ) {
        parsedState.modalFilters.distinction = "";
      }
      return parsedState;
    }
    return defaultState;
  });

  const updateState = useCallback(
    (newState: Partial<SelectionState>) => {
      const updated = { ...selectionState, ...newState };
      setSelectionState(updated);
      localStorage.setItem("productionPlanSelection", JSON.stringify(updated));
    },
    [selectionState],
  );

  const setSelectedBOMRows = useCallback(
    (rows: any[]) => {
      updateState({ selectedBOMRows: rows });
    },
    [updateState],
  );

  const setModalFilters = useCallback(
    (filters: SelectionState["modalFilters"]) => {
      updateState({ modalFilters: filters });
    },
    [updateState],
  );

  const setModalCurrentPage = useCallback(
    (page: number) => {
      updateState({ modalCurrentPage: page });
    },
    [updateState],
  );

  const setModalPageSize = useCallback(
    (size: number) => {
      updateState({ modalPageSize: size });
    },
    [updateState],
  );

  const clearSelection = useCallback(() => {
    setSelectionState(defaultState);
    localStorage.removeItem("productionPlanSelection");
  }, []);

  return (
    <SelectionContext.Provider
      value={{
        selectionState,
        setSelectedBOMRows,
        setModalFilters,
        setModalCurrentPage,
        setModalPageSize,
        clearSelection,
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
};

export const useSelection = () => {
  const context = useContext(SelectionContext);
  if (context === undefined) {
    throw new Error("useSelection must be used within a SelectionProvider");
  }
  return context;
};
